import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import DocumentGenerationStep from '../../components/lawsuit/DocumentGenerationStep';
import axios from 'axios';
import type { Lawsuit as SharedLawsuit } from '@tvia-ai/shared';

const GenerateDocumentsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Fetch lawsuit details using React Query
  const {
    data: lawsuit,
    isLoading,
    error,
  } = useQuery<SharedLawsuit | undefined>({
    queryKey: ['lawsuit', id],
    queryFn: async () => {
      if (!id) return undefined;
      const response = await axios.get(`/api/lawsuits/${id}`);
      return response.data.data.lawsuit;
    },
    enabled: !!id,
    retry: 1,
  });

  const handleBack = () => {
    navigate(`/lawsuit/${id}`);
  };

  const handleDocumentsGenerated = () => {
    // Navigate back to lawsuit detail page after documents are generated
    navigate(`/lawsuit/${id}`, { 
      state: { 
        message: t('lawsuit.documentsGeneratedSuccess', 'Documents generated successfully!') 
      } 
    });
  };

  const handleUpgradeToPremium = () => {
    // Navigate to premium upgrade page
    window.location.href = '/pricing';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !lawsuit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('common.error', 'Error')}
          </h1>
          <p className="text-gray-600 mb-4">
            {t('lawsuit.notFound', 'Lawsuit not found or you do not have permission to view it.')}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            {t('common.backToDashboard', 'Back to Dashboard')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('lawsuit.generateDocuments', 'Generate Documents')} - {lawsuit.aiAnalysis?.caseTitle || t('lawsuit.lawsuit', 'Lawsuit')}</title>
        <meta name="description" content={t('lawsuit.generateDocumentsDescription', 'Generate additional legal documents for your lawsuit case')} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={handleBack}
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 mb-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              {t('common.back', 'Back to case')}
            </button>
            
            <div className="bg-white shadow rounded-lg px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {t('lawsuit.generateDocuments', 'Generate Documents')}
                  </h1>
                  <p className="text-gray-600 mt-1">
                    {lawsuit.aiAnalysis?.caseTitle || t('lawsuit.caseTitle', 'Legal Case')}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {lawsuit.country}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t(`categories.${lawsuit.category}`, lawsuit.category)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Document Generation Component */}
          <DocumentGenerationStep
            lawsuitId={id!}
            onBack={handleBack}
            onDocumentsGenerated={handleDocumentsGenerated}
            isPremiumMode={lawsuit.aiAnalysis?.isPremiumAnalysis || false}
            onUpgradeToPremium={handleUpgradeToPremium}
            isEditMode={false}
          />
        </div>
      </div>
    </>
  );
};

export default GenerateDocumentsPage;